<template>
  <div>
    <LoadingVehicle v-if="saving" />
    <QForm v-else @submit="saveOrderDetails">
      <h6 class="q-mt-none q-mb-md text-grey-9">Order Details</h6>
      <FormError :show="$v.$error && $v.$dirty" class="q-my-md" />
      <p class="q-mb-md text-weight-bold text-grey-9">
        Company Details
      </p>
      <div class="row q-col-gutter-md">
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Company Name*
          </p>
          <QInput
            v-model="$v.companyName.$model"
            filled
            maxlength="50"
            color="secondary"
            dense
            :disable="isExpired"
            hide-bottom-space
            clearable
            :error="$v.companyName.$error"
          />
        </div>
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            ABN / ACN*
          </p>
          <QInput
            v-model="$v.companyAbn.$model"
            filled
            maxlength="11"
            color="secondary"
            dense
            :disable="isExpired"
            hide-bottom-space
            clearable
            :error="$v.companyAbn.$error"
          />
        </div>
      </div>
      <QSeparator class="q-my-md" />
      <p class="q-mb-md text-weight-bold text-grey-9">
        Contact Details
      </p>
      <div class="row q-col-gutter-md">
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Customer Name*
          </p>
          <QInput
            v-model="$v.customerName.$model"
            filled
            maxlength="50"
            color="secondary"
            dense
            :disable="isExpired"
            hide-bottom-space
            clearable
            :error="$v.customerName.$error"
          />
        </div>
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Contact Number*
          </p>
          <QInput
            v-model="$v.customerContactNumber.$model"
            filled
            type="tel"
            pattern="[- +0-9]+"
            title="Please enter a valid Australian phone number"
            color="secondary"
            dense
            :disable="isExpired"
            hide-bottom-space
            clearable
            :error="$v.customerContactNumber.$error"
          />
        </div>
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Contact Email*
          </p>
          <QInput
            v-model="$v.customerContactEmail.$model"
            filled
            type="email"
            color="secondary"
            dense
            :disable="isExpired"
            hide-bottom-space
            clearable
            :error="$v.customerContactEmail.$error"
          />
        </div>
      </div>
      <QSeparator class="q-my-md" />
      <p class="q-mb-md text-weight-bold text-grey-9">
        Delivery Address
      </p>
      <div class="row q-col-gutter-md">
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Address Line 1*
          </p>
          <QInput
            v-model="$v.deliveryAddressLine1.$model"
            filled
            color="secondary"
            dense
            :disable="isExpired"
            clearable
            :error="$v.deliveryAddressLine1.$error"
            hide-bottom-space
          />
        </div>
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Address Line 2
          </p>
          <QInput
            v-model="$v.deliveryAddressLine2.$model"
            filled
            color="secondary"
            dense
            :disable="isExpired"
            clearable
            :error="$v.deliveryAddressLine2.$error"
            hide-bottom-space
          />
        </div>
        <div class="col-4">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Suburb*
          </p>
          <QInput
            v-model="$v.deliverySuburb.$model"
            filled
            color="secondary"
            dense
            :disable="isExpired"
            clearable
            :error="$v.deliverySuburb.$error"
            hide-bottom-space
          />
        </div>
        <div class="col-4">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            State*
          </p>
          <StateSelect
            v-model="$v.deliveryState.$model"
            filled
            color="secondary"
            dense
            :disable="isExpired"
            :error="$v.deliveryState.$error"
            hide-bottom-space
          />
        </div>
        <div class="col-4">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Postcode*
          </p>
          <QInput
            v-model="$v.deliveryPostcode.$model"
            input-class="hide-input-arrows"
            filled
            max="9999"
            min="0"
            type="number"
            color="secondary"
            dense
            :disable="isExpired"
            clearable
            :error="$v.deliveryPostcode.$error"
            hide-bottom-space
          />
        </div>
        <div class="col-6">
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Delivery Date*
          </p>
          <EUIDateInput
            v-model="$v.deliveryDate.$model"
            filled
            color="secondary"
            dateMask="DD/MM/YYYY"
            dense
            :disable="isExpired"
            hide-bottom-space
            :error="$v.deliveryDate.$error"
          />
        </div>
      </div>
      <QSeparator class="q-my-md" />
      <div class="row q-col-gutter-md">
        <div class="col-6">
          <p class="q-mb-md text-weight-bold text-grey-9">
            Dealership
          </p>
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Dealership*
          </p>
          <QSelect
            ref="dealerSelect"
            v-model="dealerSearch"
            filled
            color="secondary"
            dense
            :disable="isExpired"
            :options="dealers.rows"
            clearable
            use-input
            popup-content-class="limit-q-menu-height"
            fill-input
            hide-selected
            @virtual-scroll="dealerSearchScrolled"
            @popup-hide="dealerSearchClosed"
            @input="selectDealer"
            @input-value="searchDealers"
            option-label="name"
            :disabled="dealerSearchCreating"
            :loading="$apollo.queries.dealers.loading"
            :error="$v.dealership.$error"
          >
            <template slot="no-option">
              <QItem v-if="!dealerSearch">
                <QItemSection>
                  <QItemLabel>
                    Search for a dealer.
                  </QItemLabel>
                </QItemSection>
              </QItem>
              <QItem
                v-else-if="!$apollo.queries.dealers.loading"
                clickable
                @click="addNewDealer"
                class="cursor-pointer"
              >
                <QItemSection>
                  <QItemLabel class="text-grey-7 items-center row">
                    <QIcon
                      :name="plusIcon"
                      class="q-mr-sm"
                      color="secondary"
                      size="20px"
                    />
                    <span class="text-secondary col-grow">
                    Add this dealership
                  </span>
                    <QSpinner
                      color="secondary"
                      size="xs"
                      v-if="dealerSearchCreating"
                    />
                  </QItemLabel>
                </QItemSection>
              </QItem>
            </template>
          </QSelect>
        </div>
        <div class="col-6">
          <p class="q-mb-md text-weight-bold text-grey-9">
            Registration Suburb
          </p>
          <p class="text-body3 text-grey-7 q-mb-xs text-weight-medium">
            Postcode*
          </p>
          <QInput
            v-model="$v.registrationPostcode.$model"
            input-class="hide-input-arrows"
            filled
            max="9999"
            type="number"
            color="secondary"
            dense
            :disable="isExpired"
            clearable
            :error="$v.registrationPostcode.$error"
          />
        </div>
      </div>
      <QuoteNavigationActions back-to="quoteColourAndExtras">
        <template slot="next">
          <QBtn
            type="submit"
            :label="isExpired ? 'Next' : 'Save and Next'"
            :icon-right="this.nextIcon"
            color="secondary"
            :loading="saving"
            :disable="saving"
            :ripple="false"
          />
        </template>
      </QuoteNavigationActions>
    </QForm>
  </div>
</template>

<script>
import {
  QForm,
  QInput,
  QSeparator,
  EUIDateInput,
  Icons,
  QSelect,
  QBtn,
  QItem,
  QItemSection,
  QItemLabel,
  QIcon,
  QSpinner,
  date,
  EnGbLang
} from 'eclipx.ui';
import {mapState, mapActions, mapGetters} from 'vuex';
import {required} from 'vuelidate/lib/validators';
import QuoteNavigationActions from '../QuoteNavigationActions';
import StateSelect from '../../../components/StateSelect/StateSelect';
import FormError from '../../../components/FormError/FormError';
import QueryDealers from '../../../graphql/queries/QueryDealers';
import MutateDealer from '../../../graphql/mutations/MutateDealer';
import {GLOBAL_ERROR} from '../../../components/GlobalError/GlobalError';
import MutateQuote from '../../../graphql/mutations/MutateQuote';
import LoadingVehicle from '../../../components/LoadingVehicle/LoadingVehicle';

export default {
  name: 'OrderDetails',
  data() {
    return {
      saving: false,
      companyName: null,
      companyAbn: null,
      customerName: null,
      customerContactNumber: null,
      customerContactEmail: null,
      deliveryAddressLine1: null,
      deliveryAddressLine2: null,
      deliverySuburb: null,
      deliveryState: null,
      deliveryPostcode: null,
      deliveryDate: null,
      dealership: null,
      registrationPostcode: null,
      dealers: [],
      dealerSearch: null,
      dealerSearchCreating: false,
      dealerQuerySize: 20
    };
  },
  validations: {
    companyName: {required},
    companyAbn: {required},
    customerName: {required},
    customerContactNumber: {required},
    customerContactEmail: {required},
    deliveryAddressLine1: {required},
    deliveryAddressLine2: {},
    deliverySuburb: {required},
    deliveryState: {required},
    deliveryPostcode: {required},
    deliveryDate: {required},
    dealership: {required},
    registrationPostcode: {required}
  },
  computed: {
    ...mapState({erpquote: (state) => state.quote.quote.erpquote}),
    ...mapGetters({
      isExpired: 'quote/isExpired',
      saveQuoteFields: 'quote/saveQuoteFields'
    }),
    dealerSearchQueryVariables: function() {
      let params = {
        orgId: this.$store.getters['auth/user'].organisation.id,
        size: this.dealerQuerySize
      };
      if (this.dealerSearch) {
        params.query = this.dealerSearch;
      }
      return {params};
    }
  },
  watch: {
    // If the dealership changes, ensure the dealership search text matches
    dealership: function(value) {
      this.dealerSearch = value;
    },
    // Ensure the dealership select list is refreshed and shows the first result
    dealers: function() {
      this.$refs.dealerSelect.refresh();
    }
  },
  apollo: {
    dealers: {
      query: QueryDealers,
      debounce: 300,
      fetchPolicy: 'network-only',
      variables() {
        return this.dealerSearchQueryVariables;
      },
      error: function() {
        this.$euiNotifications.emit({
          notification: GLOBAL_ERROR,
          action: () => {
            return this.$apollo.queries.dealers.refresh();
          }
        });
      }
    }
  },
  methods: {
    ...mapActions({saveCustomerDetails: 'quote/saveCustomerDetails'}),
    goToNext() {
      this.$router.push({
        name: 'quoteReview',
        params: {quoteId: this.$route.params.quoteId}
      });
    },
    saveOrderDetails: async function() {
      this.$v.$touch();
      if (this.$v.$invalid) {
        return;
      }

      if (!this.isExpired) {

        let leaseUpdatedValues = {
          leaseTerm: this.erpquote.leaseTerm,
          kmsPerYear: this.erpquote.kmsPerYear,
          availableOptions: this.erpquote.availableOptions,
          leaseStart: this.erpquote.leaseStart,
          leaseEnd: this.erpquote.leaseEnd
        };

        let colourAndExtrasUpdatedValues = {
          exteriorColour: this.erpquote.exteriorColour,
          interiorColour: this.erpquote.interiorColour,
          optionalEquipment: this.erpquote.optionalEquipment,
          adhocEquipment: this.erpquote.adhocEquipment,
          price: this.erpquote.price
        };


        let customerUpdatedValues = {
          customer: {
            companyName: this.companyName,
            companyAbn: this.companyAbn,
            customerName: this.customerName,
            customerContactNumber: this.customerContactNumber,
            customerContactEmail: this.customerContactEmail,
            deliveryAddressLine1: this.deliveryAddressLine1,
            deliveryAddressLine2: this.deliveryAddressLine2,
            deliverySuburb: this.deliverySuburb,
            deliveryState: this.deliveryState,
            deliveryPostcode: this.deliveryPostcode,
            deliveryDate: date.formatDate(
              date.extractDate(this.deliveryDate, 'DD/MM/YYYY', EnGbLang.date),
              'YYYY-MM-DD',
              EnGbLang.date
            ),
            dealership: this.dealership,
            registrationPostcode: this.registrationPostcode
          }
        };

        let updatedValues = {
          ...leaseUpdatedValues,
          ...colourAndExtrasUpdatedValues,
          ...customerUpdatedValues
        };

        this.saving = true;
        return await this.$apollo
          .mutate({
            mutation: MutateQuote,
            variables: {
              params: {
                ...this.saveQuoteFields,
                erpQuoteData: {
                  ...this.erpquote,
                  ...updatedValues
                }
              }
            },
            update: (store, response) => {
              if (response.data.saveQuote) {
                this.saveCustomerDetails({
                  ...response.data.saveQuote.response,
                  ...customerUpdatedValues
                });
                this.goToNext();
              }
            }
          })
          .catch(() => {
            this.$euiNotifications.emit({
              notification: GLOBAL_ERROR,
              action: () => {
                return this.saveOrderDetails();
              }
            });
          })
          .finally(() => {
            this.saving = false;
          });
      } else {
        this.goToNext();
      }
    },
    dealerSearchScrolled: async function({index, to}) {
      let totalSelectCount = to + 1;
      // Do nothing if there were no rows, the query for new rows is in progress, the user did not scroll to the end or
      // the total number of potential rows have been fetched
      if (
        to < 1 ||
        this.$apollo.queries.dealers.loading ||
        index !== to ||
        totalSelectCount >= this.dealers.count
      ) {
        return;
      }
      let variables = this.dealerSearchQueryVariables;
      variables.params.page = parseInt(totalSelectCount / this.dealerQuerySize);
      await this.$apollo.queries.dealers.fetchMore({
        variables,
        updateQuery: (previousResult, {fetchMoreResult}) => {
          return {
            dealers: {
              count: fetchMoreResult.dealers.count,
              rows: [...this.dealers.rows, ...fetchMoreResult.dealers.rows],
              __typename: fetchMoreResult.dealers.__typename
            }
          };
        }
      });
      this.$nextTick(function() {
        this.$refs.dealerSelect.refresh();
      });
    },
    dealerSearchClosed: function() {
      if (this.dealership && this.dealership.id) {
        this.dealerSearch = this.dealership.name;
      }
    },
    selectDealer: function(value = {}) {
      this.dealership = value.name;
      this.dealerSearch = value ? value.name : null;
    },
    searchDealers: function(value) {
      this.dealerSearch = value;
    },
    addNewDealer: async function() {
      this.dealerSearchCreating = true;

      return await this.$apollo
        .mutate({
          mutation: MutateDealer,
          variables: {
            params: {
              orgId: this.$store.getters['auth/user'].organisation.id,
              name: this.dealerSearch
            }
          },
          update: (store, response) => {
            if (response.data.createDealer) {
              const dealer = response.data.createDealer.dealer.name;
              store.writeQuery({
                query: QueryDealers,
                data: {
                  dealers: {
                    count: 1,
                    rows: [dealer],
                    __typename: 'DealerResponse'
                  }
                }
              });
              this.$refs.dealerSelect.hidePopup();
              this.dealership = dealer;
              this.dealers = {count: 1, rows: [dealer]};
            }
            this.dealerSearchCreating = false;
          }
        })
        .catch(() => {
          this.dealerSearchCreating = false;
          this.$euiNotifications.emit({
            notification: GLOBAL_ERROR,
            action: () => {
              return this.addNewDealer();
            }
          });
        });
    }
  },
  created() {
    // If the prior page is invalid, go to the first incomplete page
    if (!this.$store.getters['quote/quoteColourAndExtrasValid']) {
      this.$router.replace({
        name: this.$store.getters['quote/firstIncompletePage'],
        params: {quoteId: this.$route.params.quoteId}
      });
    }

    this.companyName = this.erpquote.customer.companyName;
    this.companyAbn = this.erpquote.customer.companyAbn;
    this.customerName = this.erpquote.customer.customerName;
    this.customerContactNumber = this.erpquote.customer.customerContactNumber;
    this.customerContactEmail = this.erpquote.customer.customerContactEmail;
    this.deliveryAddressLine1 = this.erpquote.customer.deliveryAddressLine1;
    this.deliveryAddressLine2 = this.erpquote.customer.deliveryAddressLine2;
    this.deliverySuburb = this.erpquote.customer.deliverySuburb;
    this.deliveryState = this.erpquote.customer.deliveryState;
    this.deliveryPostcode = this.erpquote.customer.deliveryPostcode;
    let deliveryDate = this.erpquote.customer.deliveryDate
      ? date.extractDate(this.erpquote.customer.deliveryDate, 'YYYY-MM-DD', EnGbLang.date)
      : date.extractDate(this.erpquote.leaseStart, 'YYYY-MM-DD', EnGbLang.date);
    this.deliveryDate = date.formatDate(deliveryDate, 'DD/MM/YYYY', EnGbLang.date);
    this.dealership = this.erpquote.customer.dealership;
    this.registrationPostcode = this.erpquote.customer.registrationPostcode;

    this.nextIcon = Icons.evaArrowForward;
    this.plusIcon = Icons.evaPlusCircle;
  },
  components: {
    QForm,
    QInput,
    QSeparator,
    EUIDateInput,
    QuoteNavigationActions,
    QBtn,
    StateSelect,
    FormError,
    QSelect,
    QItem,
    QItemSection,
    QItemLabel,
    QIcon,
    QSpinner,
    LoadingVehicle
  }
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style lang="sass" scoped></style>
